"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Magnetic from "./ui/Magnetic";

interface GithubStats {
  followers: number;
  public_repos: number;
  stars: number;
  languages: string[];
}

export default function GithubSection() {
  const [stats, setStats] = useState<GithubStats>({
    followers: 120,
    public_repos: 42,
    stars: 154,
    languages: ["TypeScript", "JavaScript", "Vue.js", "Node.js", "Python", "HTML/CSS"]
  });
  const [loading, setLoading] = useState(true);

  const [contributionCells] = useState<number[]>(() => {
    // Generate simulated contribution cells for a 52x7 calendar (last 365 days)
    const distribution = [0, 0, 0, 0, 1, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4];
    const grid = [];
    for (let i = 0; i < 52 * 7; i++) {
      const idx = Math.floor(Math.random() * distribution.length);
      grid.push(distribution[idx]);
    }
    return grid;
  });

  interface GitHubRepo {
    stargazers_count?: number;
    language?: string;
  }

  useEffect(() => {

    const fetchGithubData = async () => {
      try {
        const userRes = await fetch("https://api.github.com/users/CodeByAmrit");
        if (!userRes.ok) throw new Error("API Limit or Error");
        const userData = await userRes.json();

        const reposRes = await fetch("https://api.github.com/users/CodeByAmrit/repos?per_page=100");
        let starsCount = 154; // Fallback baseline
        let langList: string[] = [];
        
        if (reposRes.ok) {
          const reposData = await reposRes.json();
          starsCount = reposData.reduce((acc: number, curr: GitHubRepo) => acc + (curr.stargazers_count || 0), 0);
          
          const langs = reposData.reduce((acc: { [key: string]: number }, curr: GitHubRepo) => {
            if (curr.language) acc[curr.language] = (acc[curr.language] || 0) + 1;
            return acc;
          }, {});
          
          langList = Object.keys(langs).sort((a, b) => langs[b] - langs[a]).slice(0, 6);
        }

        setStats(prev => ({
          followers: userData.followers || 120,
          public_repos: userData.public_repos || 42,
          stars: starsCount || 154,
          languages: langList.length ? langList : prev.languages
        }));
      } catch (err) {
        console.warn("GitHub API rate limited or offline. Using baseline stats.", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGithubData();
  }, []);

  return (
    <section id="github" className="section-pad relative z-10 bg-black/20 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block">Activity</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 font-[var(--font-poppins)] tracking-tighter">
            Open Source <span className="gradient-text">Node</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Reflecting active development output. Integrating real-time metrics and dynamic contributions streams from my GitHub workspace.
          </p>
        </motion.div>

        {/* Bento grid panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Contribution Calendar Grid */}
          <div className="lg:col-span-8 bg-[#05030e] border border-white/10 rounded-[2.5rem] p-6 md:p-8 flex flex-col justify-between shadow-2xl relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none -mr-32 -mt-32" />
            
            <div className="flex items-center justify-between mb-8 relative z-10">
              <div>
                <h3 className="text-xl font-black text-white font-[var(--font-poppins)] uppercase tracking-wider">
                  Contribution Heatmap
                </h3>
                <span className="text-[10px] text-gray-400 font-mono">Simulated commit activity (past 365 days)</span>
              </div>
              <Magnetic>
                <a
                  href="https://github.com/CodeByAmrit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white hover:bg-white/10 transition-colors"
                >
                  Follow Node
                </a>
              </Magnetic>
            </div>

            {/* Grid display */}
            <div className="overflow-x-auto pb-4 custom-scrollbar select-none">
              <div className="grid grid-flow-col grid-rows-7 gap-[3px] min-w-[720px] justify-start">
                {contributionCells.map((level, idx) => {
                  let colorClass = "bg-white/[0.03]";
                  if (level === 1) colorClass = "bg-indigo-950/40 text-indigo-400";
                  if (level === 2) colorClass = "bg-indigo-900/60";
                  if (level === 3) colorClass = "bg-indigo-600/80";
                  if (level === 4) colorClass = "bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]";
                  return (
                    <div
                      key={idx}
                      className={`w-[10px] h-[10px] rounded-[2px] transition-colors hover:scale-125 duration-100 ${colorClass}`}
                      title={`Activity index: ${level}`}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 text-[9px] text-gray-400 font-mono relative z-10">
              <span>Jan 2025</span>
              <span>Jun 2025</span>
              <span>Jan 2026</span>
              <div className="flex items-center gap-1.5">
                <span>Less</span>
                <span className="w-2.5 h-2.5 rounded-[2px] bg-white/[0.03]" />
                <span className="w-2.5 h-2.5 rounded-[2px] bg-indigo-950/40" />
                <span className="w-2.5 h-2.5 rounded-[2px] bg-indigo-900/60" />
                <span className="w-2.5 h-2.5 rounded-[2px] bg-indigo-600/80" />
                <span className="w-2.5 h-2.5 rounded-[2px] bg-indigo-500" />
                <span>More</span>
              </div>
            </div>
          </div>

          {/* Quick Metrics stats Panel */}
          <div className="lg:col-span-4 bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-8 md:p-10 shadow-2xl relative overflow-hidden flex flex-col gap-6">
            <h3 className="text-xl font-black text-white font-[var(--font-poppins)] uppercase tracking-wider mb-2">
              Node Parameters
            </h3>

            {loading ? (
              <div className="space-y-4 py-8 animate-pulse">
                <div className="h-10 bg-white/5 rounded-2xl" />
                <div className="h-10 bg-white/5 rounded-2xl" />
                <div className="h-10 bg-white/5 rounded-2xl" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Repository Stars</span>
                  <span className="text-lg font-bold text-indigo-400 font-mono">{stats.stars}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Public Repositories</span>
                  <span className="text-lg font-bold text-purple-400 font-mono">{stats.public_repos}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Active Followers</span>
                  <span className="text-lg font-bold text-pink-400 font-mono">{stats.followers}</span>
                </div>

                <div className="h-px bg-white/5 my-4" />
                <h4 className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Primary Language Nodes:</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {stats.languages.map((lang) => (
                    <span
                      key={lang}
                      className="text-[9px] px-2.5 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-bold uppercase tracking-wider"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
